import { lazy, Suspense } from 'react';
import { Routes, Route, NavLink, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import './pursue.css';

const Pursue = lazy(() => import('./Pursue').then(m => ({ default: m.Pursue })));
const DualArchive = lazy(() => import('./DualArchive').then(m => ({ default: m.DualArchive })));
const PursueAtlas = lazy(() => import('./PursueAtlas').then(m => ({ default: m.PursueAtlas })));

interface PursueAppProps {
  /** Route prefix where this app is mounted: '/pursue' embedded, '' standalone (root). */
  base?: string;
  /** Where the "exit" link points (v1 dashboard embedded, main site standalone). */
  homeHref?: string;
}

/**
 * PURSUE v2 front. Embedded build mounts it at /pursue/* (base='/pursue', inside
 * App.tsx, OUTSIDE the v1 <Layout>); the standalone build mounts it at root
 * (base=''). Owns its own dark-archive shell + nested router. v1 is untouched.
 */
export function PursueApp({ base = '/pursue', homeHref = '/' }: PursueAppProps = {}) {
  const { t } = useTranslation();
  const root = base || '/';
  const tabs = [
    { idx: '01', to: root, key: 'overview', end: true },
    { idx: '02', to: `${base}/dual`, key: 'dual', end: false },
    { idx: '03', to: `${base}/atlas`, key: 'atlas', end: false },
  ];
  return (
    <div className="pursue-root">
      <header className="pursue-header">
        <Link to={root} className="pursue-logo">
          <span className="pursue-logo-main">PURSUE</span>
          <span className="pursue-logo-sub">{t('pursue.logoSub')}</span>
        </Link>

        <nav className="pursue-tabs" aria-label="PURSUE surfaces">
          {tabs.map(tab => (
            <NavLink
              key={tab.key}
              to={tab.to}
              end={tab.end}
              className={({ isActive }) => 'pursue-tab' + (isActive ? ' active' : '')}
            >
              <span className="idx">{tab.idx}</span>
              {t(`pursue.nav.${tab.key}`)}
            </NavLink>
          ))}
        </nav>

        <div style={{ display: 'flex', gap: '14px', alignItems: 'center' }}>
          <div className="pursue-header-meta">
            <span>{t('pursue.fiscalYear')}</span>
            <span className="clr">
              <span className="status-dot" /> {t('pursue.status')}
            </span>
          </div>
          <Link to={homeHref} className="pursue-exit">
            {t('pursue.nav.exit')}
          </Link>
        </div>
      </header>

      <main id="pursue-main">
        <Suspense fallback={<div className="pursue-stub" aria-label="Loading" />}>
          <Routes>
            <Route index element={<Pursue />} />
            <Route path="dual" element={<DualArchive />} />
            <Route path="atlas" element={<PursueAtlas />} />
            <Route path="*" element={<Pursue />} />
          </Routes>
        </Suspense>
      </main>

      <footer className="site-foot">
        <div className="site-foot-inner">
          <div>
            <div className="site-foot-brand">{t('pursue.footer.brand')}</div>
            <div className="site-foot-meta" style={{ marginTop: '10px' }}>
              {t('pursue.footer.meta')}
            </div>
          </div>
          <p className="site-foot-note">{t('pursue.footer.note')}</p>
        </div>
      </footer>
    </div>
  );
}
